import {Injectable, OnDestroy} from "@angular/core";
import {NgRedux} from "@angular-redux/store";
import {Observable, of, Subject} from "rxjs";
import {distinctUntilChanged, map, shareReplay, tap, catchError, takeUntil} from "rxjs/operators";
import {IAppState} from "../../store/IAppState";
import {PlanningBoardActions} from "../planningBoard.action";
import {PlanningBlock} from "../../pb-screen/models/planning-block/planning-block.interface";
import {DeleteToFromCapacity, TransportOrder} from "../../pb-screen/models/transport-order/transport-order.interface";
import {SelectionType, SelectionTypes} from "../../static/selection-types";
import {Router} from "@angular/router";
import {ApplicationUser} from "../../pb-screen/models/application.user.interface";
import {WizardDefinition} from "../../shared/models/wizard-definition";
import {OrderLine} from "../../pb-screen/models/order-line/orderLine.interface";
import {WindowsService} from "../../shared/models/windows-service";
import {PlanningConflict} from "../../pb-screen/models/planning-conflict.interface";
import {Quote} from "../../static/models/quote.interface";
import {PlanningOrchestrationService, PlanningInput, PlanningResult, DateValidationResult} from "../../shared/services/planning-orchestration.service";
import {CapacitySlot} from "../../shared/models/capacity-slot.interface";
import {Driver} from "../../static/models/driver.interface";
import {DevLoggerService} from "../../shared/services/dev-logger.service";
import {UserFeedbackService} from "../../services/user-feedback.service";
import {PlanningSignalRService} from "./planning-signalr.service";
import {ActionKeys} from "../../pb-screen/action.keys";
import {
  OptimisticPlanResponse,
  PlanningConfirmedPayload,
  PlanningFailedPayload,
  OptimisticPlanningBlock,
  OptimisticTransportOrder
} from "../../shared/models/optimistic";
import {environment} from "../../../environments/environment";

@Injectable()
export class PlanningBoardFacade implements OnDestroy {
  private destroy$ = new Subject<void>();
  private signalRInitialized = false;

  constructor(
    private readonly store: NgRedux<IAppState>,
    private readonly planningBoardActions: PlanningBoardActions,
    private readonly router: Router,
    private readonly planningOrchestration: PlanningOrchestrationService,
    private readonly devLogger: DevLoggerService,
    private readonly userFeedbackService: UserFeedbackService,
    private readonly planningSignalR: PlanningSignalRService,
  ) {
    // Subscribe to orchestration service's optimistic events
    this.subscribeToOrchestrationEvents();
  }

  public user: ApplicationUser;
  // ===============
  // Store selections (placeholders) — add/rename as you migrate components
  // Use shareReplay(1) where multiple consumers may subscribe to derived streams.
  // ===============

  applicationUser$: Observable<ApplicationUser> = this.store.select<ApplicationUser>("applicationUser").pipe(
    distinctUntilChanged(),
    shareReplay({bufferSize: 1, refCount: true})
  );

  loading$ = this.store.select<boolean>("loading").pipe(
    distinctUntilChanged(),
    shareReplay({bufferSize: 1, refCount: true})
  );

  orderlinesDockLoading$ = this.store.select<boolean>("orderlinesDockLoading").pipe(
    distinctUntilChanged(),
    shareReplay({bufferSize: 1, refCount: true})
  );

  selectedTransportOrders$ = this.store.select<TransportOrder[]>("selectedTransportOrders").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  selectedPbsFromPlanBoard$: Observable<PlanningBlock[]> = this.store.select<PlanningBlock[]>("selectedPbsFromPlanBoard").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  selectedPbsToPlan$: Observable<PlanningBlock[]> = this.store.select<PlanningBlock[]>("selectedPbsToPlan").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  selectedPlanningBlocks$: Observable<PlanningBlock[]> = this.store.select<PlanningBlock[]>("selectedPlanningBlocks").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  unplannedPbs$: Observable<PlanningBlock[]> = this.store.select<PlanningBlock[]>("unplannedPbs").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  unplannedPbsForWeekPlanning$: Observable<PlanningBlock[]> = this.store.select<PlanningBlock[]>("unplannedPbsForWeekPlanning").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  orderLines$: Observable<OrderLine[]> = this.store.select<OrderLine[]>("orderLines").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  selectedOrderlines$: Observable<OrderLine[]> = this.store.select<OrderLine[]>("selectedOrderlines").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  selectedOrderlinesFromBoard$: Observable<OrderLine[]> = this.store.select<OrderLine[]>("selectedOrderlinesFromBoard").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  pbWizardDefinitions$: Observable<WizardDefinition[]> = this.store.select<WizardDefinition[]>("pbWizardDefinitions").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  toWizardDefinitions$: Observable<WizardDefinition[]> = this.store.select<WizardDefinition[]>("toWizardDefinitions").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  selectedQuotes$ = this.store.select<Quote[]>("selectedQuotes").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  selectedQuotelinesFromBoard$ = this.store.select<any[]>("selectedQuotelinesFromBoard").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  selectedCapacityFromPlanBoard$ = this.store.select("selectedCapacityFromPlanBoard").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  selectionType$ = this.store.select<string>("selectionType").pipe(
    distinctUntilChanged(),
    shareReplay({bufferSize: 1, refCount: true})
  );

  planningData$ = this.store.select<any[]>("planningData").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  weekPlanningData$ = this.store.select<any>("weekPlanningData").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  openTosForDate$ = this.store.select<TransportOrder[]>("openTosForDate").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  planBoardMonitor$ = this.store.select<WindowsService>("planBoardMonitor").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  planningConflictsForDock$ = this.store.select<PlanningConflict[]>("planningConflictsForDock").pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );


  get applicationUser(): ApplicationUser {
    const state = this.store.getState() as any;
    return state?.applicationUser as ApplicationUser;
  }

  // Synchronous accessor for the current selection type (non-observable)
  get selectionType(): SelectionType {
    const state = this.store.getState() as any;
    return state?.selectionType as SelectionType;
  }

  get selectedPbsFromPlanBoard(): PlanningBlock[] {
    const state = this.store.getState() as any;
    return state?.selectedPbsFromPlanBoard;
  }

  get selectedOrderLinesFromBoard(): OrderLine[] {
    const state = this.store.getState() as any;
    return (state?.selectedOrderlinesFromBoard as OrderLine[]) ?? [];
  }

  // Placeholder: add more as you need
  // e.g. pbOrderlines$, orderLines$, chassisTrackAndTrace$, etc.
  // pbOrderlines$ = this.store.select('pbOrderlines').pipe(shareReplay({ bufferSize: 1, refCount: true }));
  // orderLines$ = this.store.select('orderLines').pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // ===============
  // Derived view-model streams (examples)
  // Extract repeated computations into the facade and share.
  // ===============

  // Whether the currently selected PBs (from either source) can be planned — simple example.
  // Replace the predicate when you wire proper status enums.
  itemsCanBePlanned$ = this.selectedPbsFromPlanBoard$.pipe(
    map((pbs: any[]) => Array.isArray(pbs) && pbs.length > 0
      ? pbs.every(pb => ["TO_BE_PLANNED", "PRE_PLANNED"].includes(pb?.status?.statusType))
      : false
    ),
    distinctUntilChanged(),
    shareReplay({bufferSize: 1, refCount: true})
  );

  // Example of a composite view model used by several components
  /*
  bottomDockOrderLinesDetailsVm$: Observable<BottomDockOrderLinesDetailsVm> = combineLatest([
    this.applicationUser$,
    this.selectedPbsFromPlanBoard$,
    this.selectedOrderlinesFromBoard$,
    this.pbWizardDefinitions$
  ]).pipe(
    map(([applicationUser,selectedPbsFromPlanBoard, selectedOrderlinesFromBoard, pbWizardDefinitions]) => ({
      applicationUser : applicationUser,
      selectedPbsFromPlanBoard: selectedPbsFromPlanBoard,
      selectedOrderlinesFromBoard: selectedOrderlinesFromBoard,
      pbWizardDefinitions: pbWizardDefinitions,
    })),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  */

  // ===============
  // Commands (wrap existing action services) — placeholders
  // Keep these intention-revealing and delegate to your existing actions.
  // ===============

  public refreshToInPlanningBoard(transportOrder: TransportOrder) {
    this.planningBoardActions.UpdatePlanningDisplay([transportOrder],false);
  }

  public refreshOnPlanningActionForPlanningBlock(transportOrder: TransportOrder, planningBlocks: PlanningBlock[]) {
    // Optimistic UI: do not hijack the user's current selection on background-task completion.
    if (!environment.enableOptimisticPlanning) {
      if (this.selectionType === SelectionTypes.OrderLine) {
        const selectedOrderlines = this.selectedOrderLinesFromBoard;
        if (selectedOrderlines && Array.isArray(selectedOrderlines) && selectedOrderlines.length > 0) {
          const selectedIds = selectedOrderlines.map((ol: any) => ol?.orderLineId);
          const filteredOrderlines = transportOrder.orderlines.filter((ol: any) => selectedIds.some(s => s == ol.orderLineId));
          this.planningBoardActions.setSelectedOrderlinesFromPlanBoard(filteredOrderlines);
        } else {
          this.planningBoardActions.setSelectedOrderlinesFromPlanBoard(transportOrder.orderlines);
        }
      }
      this.planningBoardActions.setSelectedPbsFromPlanBoard(planningBlocks);
    }
    this.planningBoardActions.UpdatePlanningDisplay([transportOrder]);
    this.stopLoadingOnBoard();
  }

  public refreshOnUnplanPlanningActionForTransportOrder(transportOrders: TransportOrder[]) {
    const deletedCapacity: DeleteToFromCapacity[] = transportOrders.map(to => ({
      tranportOrderNo: to.id,
      truckName: to.truck?.name,
      driverName: to.driver
    }));

    this.planningBoardActions.removeTosFromCapacity(deletedCapacity);

    this.stopLoadingOnBoard();
    this.clearSelection();
  }

  public refreshOnUnplanPlanningActionForLastPlanningBlock(planningBlocks: PlanningBlock[]) {
    const deletedCapacity: DeleteToFromCapacity[] = planningBlocks.map(pb => ({
      tranportOrderNo: pb.transportOrderNumber,
      truckName: pb.truck,
      driverName: pb.driver?.name
    }));

    this.planningBoardActions.removeTosFromCapacity(deletedCapacity);


    this.stopLoadingOnBoard();
    this.clearSelection();
  }

  public storeUnplannedPlanningBlocks() {
    const user = this.applicationUser;
    let dateFrom, dateUntil;
    if (this.router.url.includes("week-planning")) {
      dateFrom = user.defaultPlanDateRange.defaultPlanDateFrom;
      dateUntil = user.defaultPlanDateRange.defaultPlanDateUntil;
      this.planningBoardActions.storeUnplannedPlanningBlocksForWeekPlanning(
        user.defaultPlanZone,
        user.defaultPlanGroups.join(","),
        dateFrom,
        dateUntil,
        "");
    } else {
      dateUntil = dateFrom = user.defaultPlanDate;
      this.planningBoardActions.storeUnplannedPlanningBlocks(
        user.defaultPlanZone,
        user.defaultPlanGroups.join(","),
        dateFrom,
        dateUntil,
        "");
    }
  }

  public refreshUnplannedTransportOrder() {
    let dateFrom, dateUntil;
    if (this.router.url.includes("week-planning")) {
      dateFrom = this.user.defaultPlanDateRange.defaultPlanDateFrom;
      dateUntil = this.user.defaultPlanDateRange.defaultPlanDateUntil;
    } else {
      dateUntil = dateFrom = this.user.defaultPlanDate;
    }

    this.planningBoardActions.storeOpenTosForDate(dateFrom, dateUntil, this.user.defaultPlanGroups.join(","));
  }

  public refreshOnPlanningActionForTransportOrder(transportOrder: TransportOrder) {
    if (this.selectionType === SelectionTypes.TransportOrder) {
      this.planningBoardActions.UpdateOrderLinesDisplay(transportOrder.planningOrderlines);
      this.planningBoardActions.UpdatePlanningDisplay([transportOrder]);
    } else {
      const selectedPbs = this.selectedPbsFromPlanBoard;
      if (selectedPbs && Array.isArray(selectedPbs) && selectedPbs.length > 0) {
        const selectedIds = selectedPbs.map((pb: any) => pb?.id);
        const filteredPbs = transportOrder.planningBlocks.filter((pb: any) => selectedIds.some(s => s == pb.id));
        this.refreshOnPlanningActionForPlanningBlock(transportOrder, filteredPbs);
      }
    }
    this.stopLoadingOnBoard();
  }

  public clearSelection() {
    this.planningBoardActions.setSelectedOrderlinesFromPlanBoard([]);
    this.planningBoardActions.setSelectedPbsFromPlanBoard([]);
    this.planningBoardActions.UpdateOrderLinesDisplay([]);
    this.planningBoardActions.UpdatePlanningDisplay([], true);
    this.planningBoardActions.setSelectionType("");
  }

  public startLoadingOnBoard() {
    this.planningBoardActions.setLoading(true);
  }

  public stopLoadingOnBoard() {
    this.planningBoardActions.setLoading(false);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PLANNING OPERATIONS
  // These methods orchestrate planning workflows and will be enhanced
  // with optimistic UI support in future iterations.
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Execute a planning operation (plan PBs to a capacity slot)
   * 
   * This is the main entry point for planning operations from the UI.
   * It delegates to PlanningOrchestrationService and handles UI updates.
   * 
   * @param depotInPlanningBlocks Depot-in PBs to plan
   * @param depotOutPlanningBlocks Depot-out PBs to plan
   * @param capacitySlot Target capacity slot (truck/driver/timeslot)
   * @param planDate Planning date
   * @param userId User performing the action
   * @param options Additional options
   * @returns Observable of planning result
   */
  public executePlanning(
    depotInPlanningBlocks: PlanningBlock[],
    depotOutPlanningBlocks: PlanningBlock[],
    capacitySlot: CapacitySlot,
    planDate: Date,
    userId: string,
    options?: {
      selectedDriver?: Driver;
      existingTransportOrder?: TransportOrder;
      groupAfterPlan?: boolean;
      sendAfterGroup?: boolean;
      statusUpdate?: {
        enabled: boolean;
        status?: string;
        location?: any;
      };
    }
  ): Observable<PlanningResult> {
    this.devLogger.planning('Facade: executePlanning', {
      depotIn: depotInPlanningBlocks.length,
      depotOut: depotOutPlanningBlocks.length,
      hasExistingTO: !!options?.existingTransportOrder
    });

    // Start loading indicator
    this.startLoadingOnBoard();

    // Note: using 'as any' for type compatibility between different TransportOrder interfaces
    const input: PlanningInput = {
      depotInPlanningBlocks,
      depotOutPlanningBlocks,
      capacitySlot,
      planDate,
      userId,
      selectedDriver: options?.selectedDriver,
      existingTransportOrder: options?.existingTransportOrder as any,
      statusUpdate: options?.statusUpdate
    };

    return this.planningOrchestration.executePlanning(input, {
      groupAfterPlan: options?.groupAfterPlan ?? true,
      sendAfterGroup: options?.sendAfterGroup ?? true
    }).pipe(
      tap((result: PlanningResult) => {
        this.stopLoadingOnBoard();

        if (result.success) {
          this.devLogger.planning('Facade: Planning succeeded', { toId: result.transportOrderId });

          // Refresh the planning board display
          if (result.transportOrder) {
            this.refreshToInPlanningBoard(result.transportOrder as any);
          }

          // Refresh unplanned lists
          this.storeUnplannedPlanningBlocks();
        } else {
          this.devLogger.planning('Facade: Planning failed', { error: result.error });
        }
      }),
      catchError((error) => {
        this.stopLoadingOnBoard();
        this.devLogger.error('Facade', 'Planning error', error);
        return of({
          success: false,
          error: error?.message || 'Planning failed'
        });
      })
    );
  }

  /**
   * Validate planning dates before executing
   * 
   * Call this to check if the selected PBs can be planned on the given date.
   */
  public validatePlanningDates(
    depotInPlanningBlocks: PlanningBlock[],
    depotOutPlanningBlocks: PlanningBlock[],
    planDate: Date
  ): DateValidationResult {
    return this.planningOrchestration.validatePlanningDates({
      depotInPlanningBlocks,
      depotOutPlanningBlocks,
      capacitySlot: null, // Not needed for validation
      planDate,
      userId: ''
    });
  }

  /**
   * Check if a driver is absent on a given date
   */
  public checkDriverAbsence(
    planDate: Date,
    driverId: string
  ): Observable<{ isAbsent: boolean; absenceDetails?: any }> {
    return this.planningOrchestration.checkDriverAbsence(planDate, driverId);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // OPTIMISTIC UI METHODS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Subscribe to orchestration service events for optimistic UI updates
   */
  private subscribeToOrchestrationEvents(): void {
    // Subscribe to optimistic TO creation
    this.planningOrchestration.onOptimisticTOCreated$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((optimisticTO: OptimisticTransportOrder) => {
      this.devLogger.planning('Facade: Optimistic TO created', { id: optimisticTO.id });
      this.store.dispatch({
        type: ActionKeys.OPTIMISTIC_CREATE_TO,
        payload: optimisticTO
      });
    });

    // Subscribe to optimistic PBs creation
    this.planningOrchestration.onOptimisticPBsCreated$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((optimisticPBs: OptimisticPlanningBlock[]) => {
      this.devLogger.planning('Facade: Optimistic PBs created', { count: optimisticPBs.length });
      this.store.dispatch({
        type: ActionKeys.OPTIMISTIC_CREATE_PB,
        payload: optimisticPBs
      });
    });

    // Subscribe to optimistic confirmations
    this.planningOrchestration.onOptimisticConfirmed$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((payload: PlanningConfirmedPayload) => {
      this.handlePlanningConfirmed(payload);
    });

    // Subscribe to optimistic failures
    this.planningOrchestration.onOptimisticFailed$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((payload: PlanningFailedPayload) => {
      this.handlePlanningFailed(payload);
    });
  }

  /**
   * Initialize SignalR subscriptions for planning events
   * 
   * Call this once the user is authenticated and SignalR is connected.
   */
  public initializeSignalR(userId: string, planningGroups: string[]): void {
    if (this.signalRInitialized) {
      return;
    }

    this.devLogger.planning('Facade: Initializing SignalR', { userId, planningGroups });

    // Initialize the SignalR service
    this.planningSignalR.initialize(userId, planningGroups);

    // Subscribe to SignalR events
    this.planningSignalR.onPlanningConfirmed$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((payload: PlanningConfirmedPayload) => {
      this.handlePlanningConfirmed(payload);
    });

    this.planningSignalR.onPlanningFailed$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((payload: PlanningFailedPayload) => {
      this.handlePlanningFailed(payload);
    });

    this.signalRInitialized = true;
  }

  /**
   * Execute planning with optimistic UI (instant feedback)
   * 
   * This method:
   * 1. Validates inputs quickly
   * 2. Creates optimistic TO/PB in store
   * 3. Returns immediately (modal can close)
   * 4. Processes BC call asynchronously
   * 5. Updates via SignalR when confirmed/failed
   */
  public executePlanningOptimistic(
    depotInPlanningBlocks: PlanningBlock[],
    depotOutPlanningBlocks: PlanningBlock[],
    capacitySlot: CapacitySlot,
    planDate: Date,
    userId: string,
    options?: {
      selectedDriver?: Driver;
      existingTransportOrder?: TransportOrder;
      groupAfterPlan?: boolean;
      sendAfterGroup?: boolean;
      statusUpdate?: {
        enabled: boolean;
        status?: string;
        location?: any;
      };
    }
  ): OptimisticPlanResponse {
    this.devLogger.planning('Facade: executePlanningOptimistic', {
      depotIn: depotInPlanningBlocks.length,
      depotOut: depotOutPlanningBlocks.length,
      hasExistingTO: !!options?.existingTransportOrder
    });

    // Note: using 'as any' for type compatibility between different TransportOrder interfaces
    const input: PlanningInput = {
      depotInPlanningBlocks,
      depotOutPlanningBlocks,
      capacitySlot,
      planDate,
      userId,
      selectedDriver: options?.selectedDriver,
      existingTransportOrder: options?.existingTransportOrder as any,
      statusUpdate: options?.statusUpdate
    };

    // Execute optimistic planning - returns immediately with optimistic data
    return this.planningOrchestration.executePlanningOptimistic(input);
  }

  /**
   * Handle planning confirmation from SignalR or orchestration service
   * 
   * Called when BC confirms a planning operation.
   * Updates optimistic items with real data.
   */
  public handlePlanningConfirmed(payload: PlanningConfirmedPayload): void {
    this.devLogger.planning('Facade: handlePlanningConfirmed', {
      correlationId: payload.correlationId,
      toId: payload.transportOrder?.id
    });

    // Dispatch confirmation action to update store
    this.store.dispatch({
      type: ActionKeys.OPTIMISTIC_CONFIRM_TO,
      payload
    });

    // Show success notification
    this.userFeedbackService.showSuccessMessageWithoutTranslating(
      `Planning confirmed: TO ${payload.transportOrder?.id || 'created'}`
    );

    // Refresh unplanned lists
    this.storeUnplannedPlanningBlocks();
  }

  /**
   * Handle planning failure from SignalR or orchestration service
   * 
   * Called when BC rejects a planning operation.
   * Rolls back optimistic items and notifies user.
   */
  public handlePlanningFailed(payload: PlanningFailedPayload): void {
    this.devLogger.planning('Facade: handlePlanningFailed', {
      correlationId: payload.correlationId,
      error: payload.errorMessage
    });

    // Dispatch failure action to update store (will remove optimistic items)
    this.store.dispatch({
      type: ActionKeys.OPTIMISTIC_FAIL_TO,
      payload
    });

    // Show error notification
    this.userFeedbackService.showErrorMessageWithoutTranslating(
      `Planning failed: ${payload.errorMessage || 'Unknown error'}`
    );

    // Refresh unplanned lists to restore PBs
    this.storeUnplannedPlanningBlocks();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PHANTOM MANAGEMENT - Direct UI Optimistic Inserts
  // ═══════════════════════════════════════════════════════════════════════════

  // ═══════════════════════════════════════════════════════════════════════════
  // OPTIMISTIC STATE SELECTORS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Observable for pending optimistic operations count
   */
  public optimisticPendingCount$ = this.store.select<number>('optimisticPendingCount').pipe(
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Observable for recent failures (for displaying error states)
   */
  public optimisticRecentFailures$ = this.store.select<PlanningFailedPayload[]>('optimisticRecentFailures').pipe(
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Check if there are any pending optimistic operations
   */
  public hasPendingOptimisticOperations(): boolean {
    const state = this.store.getState();
    return (state.optimisticPendingCount || 0) > 0;
  }

  /**
   * Get pending optimistic TO by correlation ID
   */
  public getPendingOptimisticTO(correlationId: string): OptimisticTransportOrder | undefined {
    const state = this.store.getState();
    return state.optimisticPendingTOs?.get(correlationId);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // LIFECYCLE
  // ═══════════════════════════════════════════════════════════════════════════

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
